/**
 * Vaccination inbox — #551, extended to in-app uploads by #720.
 *
 * Records emailed in by customers and vets, OR uploaded by an owner inside
 * the app, machine-parsed, waiting for a person to check them. Nothing here
 * is on a dog's record until someone presses Approve.
 *
 * ── What #720 adds to this page ───────────────────────────────────────
 *
 * · A CHANNEL. A card now says how the record arrived. An in-app upload has
 *   no sender address and no subject line, so the "From" row would have
 *   rendered "Hidden" (the #537 narrowing sentence) on a record whose
 *   sender was not hidden at all — it never existed. Wrong for a different
 *   reason than a redaction, and just as much a lie (P5).
 * · An ASSESSMENT DEADLINE. Kylie pre-verifies vaccinations before every
 *   assessment; the whole reason uploads stopped being invisible is that
 *   she was retyping dates the morning a first-time dog arrived. A card
 *   whose dog has an assessment booked says so, and the page leads with a
 *   count of them, because that is the work that has a clock on it (P2 —
 *   the next action is always obvious).
 *
 * ── Design principles served (docs/DESIGN-PHILOSOPHY.md) ──────────────
 *
 * · Counter Test — the reviewer's job is "is this right? yes/no", so each
 *   card leads with the DOG and the three dates, pre-filled. The common
 *   case is one tap on Approve. Nothing requires training.
 * · P1 One glance, one truth — the dog name is the hero of each card; the
 *   pill counts and the card list come from the same response, so they
 *   cannot disagree.
 * · P2 The next action is always obvious — exactly one filled-primary
 *   button per card (Approve). When a card can't be approved yet, the
 *   button is disabled AND the blocking question is stated inline, so the
 *   card is never a dead end.
 * · P5 Never lie about data — this is the whole page. A machine guess is
 *   labelled as a machine guess ("From the document" vs "Calculated from
 *   the 3-year term"); an unstated rabies term renders as an unanswered
 *   QUESTION, not a filled field; a failed parse is visually distinct from
 *   an email that simply had no attachment. A confident-looking wrong date
 *   is the failure mode this page exists to prevent.
 * · P6 Two-layer tokens — structural `--ferro-*` spacing/radii, tenant
 *   `--brand-*` for identity and the primary action. No raw hex, no
 *   one-off spacing.
 * · P7 Built for hands — single-column card layout, ≥44px targets on
 *   every control, date inputs use the native picker, radios are real
 *   labelled hit areas. Desktop just gets more width.
 *
 * ── Term policy (#1104) ───────────────────────────────────────────────
 *
 * Ted, 2026-09-25: "For our records, Bordetella is one year, while DAPP and
 * rabies may be one or three years." The server now sends each record's
 * `termOptions` — the facility's allowed term lengths — and this page asks
 * the 1-year/3-year question on ANY vaccine whose policy is a choice, not
 * only rabies. A fixed policy asks nothing (the term is implied). When the
 * server predates #1104 and sends no `termOptions`, the page behaves
 * exactly as before: a picker on rabies, nothing elsewhere.
 *
 * ── Untrusted content ─────────────────────────────────────────────────
 *
 * `subject`, `fromEmail`, `parseRawText` and `parseNotes` are attacker-
 * controllable text from an inbound email. They are rendered as React text
 * children only — escaped by default. There is no `dangerouslySetInnerHTML`
 * anywhere in this file, and the source excerpt is presented inside a
 * clearly-labelled quote block so a reviewer reads it as *document
 * content*, never as instructions from the app.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { VaxDocumentViewer } from '../components/dog-profile/VaxDocumentViewer.js';
import { useFeatureAvailability } from '../contexts/FeatureAvailabilityContext.js';
import { VaxForwardingAddress } from '../components/vax-intake/VaxForwardingAddress.js';
import {
  fetchVaxIntakeQueue,
  approveVaxIntake,
  rejectVaxIntake,
  readSession,
  type VaxIntakeQueueResponse,
  type VaxIntakeRecord,
  type VaxIntakeSource,
  type VaxIntakeStatus,
  type VaxIntakeSubmission,
  type VaxDocumentMeta,
  type VaxIntakeTerm,
} from '../lib/api.js';

type VaxIntakeProps = { config: ClientConfig };

const FILTERS: ReadonlyArray<{
  value: VaxIntakeStatus;
  label: string;
  countKey: keyof VaxIntakeQueueResponse['counts'];
}> = [
  { value: 'pending', label: 'Needs review', countKey: 'pending' },
  { value: 'approved', label: 'Approved', countKey: 'approved' },
  { value: 'rejected', label: 'Rejected', countKey: 'rejected' },
];

const VACCINE_LABELS: Record<VaxIntakeRecord['vaccineName'], string> = {
  rabies: 'Rabies',
  bordetella: 'Bordetella',
  dhpp: 'DHPP / Distemper',
};

/** How a vaccine reads mid-sentence ("the rabies shot", "the DHPP shot"). */
const VACCINE_IN_SENTENCE: Record<VaxIntakeRecord['vaccineName'], string> = {
  rabies: 'rabies',
  bordetella: 'Bordetella',
  dhpp: 'DHPP',
};

/**
 * #1104 — the facility's allowed term lengths for a record. A server older
 * than #1104 sends no `termOptions`; fall back to exactly its behavior:
 * rabies is a 1-or-3 choice, nothing else is calculated.
 */
export function termOptionsFor(record: VaxIntakeRecord): number[] {
  if (Array.isArray(record.termOptions)) {
    return record.termOptions.filter((y) => y === 1 || y === 3);
  }
  return record.vaccineName === 'rabies' ? [1, 3] : [];
}

function yearsToTerm(years: number): VaxIntakeTerm {
  return years === 3 ? 'three_year' : 'one_year';
}

function termYears(term: VaxIntakeTerm): number {
  return term === 'three_year' ? 3 : 1;
}

function termLabel(term: VaxIntakeTerm): string {
  return term === 'three_year' ? '3-year' : '1-year';
}

/**
 * The term this record will be approved under, from the reviewer's draft:
 * the picked one on a choice, the implied one on a fixed policy, none when
 * the facility does not calculate this vaccine.
 */
export function effectiveTerm(
  record: VaxIntakeRecord,
  draftTerm: VaxIntakeTerm | '',
): VaxIntakeTerm | null {
  const options = termOptionsFor(record);
  if (options.length === 0) return null;
  if (options.length === 1) return yearsToTerm(options[0]!);
  return draftTerm || null;
}

/**
 * Machine flag → the plain-English question the reviewer has to answer.
 * Never show the raw flag key (anti-pattern: internal identifiers on
 * screen).
 */
const FLAG_PROMPTS: Record<string, string> = {
  missing_date_given: "The date given couldn't be read — enter it from the record.",
  rabies_term_unknown:
    "The record doesn't say whether this is a 1-year or 3-year rabies. Pick one.",
  term_unknown: "The record doesn't say whether this is a 1-year or 3-year shot. Pick one.",
  term_not_allowed:
    "The record states a term your facility doesn't use for this vaccine. Check the record, and the expiry, before approving.",
  expiry_underivable: 'No expiration on the record — enter when it expires.',
  date_out_of_range: "A date didn't look right and wasn't kept. Check it against the record.",
  expiry_before_date_given: 'The expiration is before the date given. Check both.',
  document_derived_mismatch:
    "The expiration printed on the record doesn't match the date the shot's term works out to. One of them is wrong — check with the vet before approving.",
};

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * The "From" line, which has to survive the server narrowing the sender
 * out of the payload (#537 — see `VaxIntakeSubmission.fromEmail`).
 *
 * Three cases, and the third is the one that matters: when `fromEmail` is
 * null the sender was WITHHELD from this reader, not missing from the
 * email. Rendering an empty `<dd>` next to a "From" label reads as "we
 * don't know who sent this", which is a different — and false — claim
 * (P5: never lie about data). Say it was hidden instead.
 *
 * #720 adds a fourth: on the customer_upload channel there is no sender at
 * all. "Hidden" there would claim a redaction that never happened, so the
 * channel is checked FIRST and the row describes what actually occurred.
 */
function formatSender(
  source: VaxIntakeSource,
  fromName: string | null,
  fromEmail: string | null,
): string {
  if (source === 'customer_upload') {
    // fromName is the owner's own name, and it goes when #537 narrows.
    return fromName ? `${fromName}, in the app` : 'Uploaded in the app';
  }
  if (fromEmail === null) return 'Hidden';
  return fromName ? `${fromName} (${fromEmail})` : fromEmail;
}

/** Label for the row that says how the record reached us. */
const SOURCE_LABELS: Record<VaxIntakeSource, string> = {
  email: 'Emailed in',
  customer_upload: 'Uploaded in the app',
};

/**
 * "Tue 11 Aug" — a date a person reads at a counter, not an ISO string.
 * `date` is a bare YYYY-MM-DD calendar day in the FACILITY's timezone, so
 * it is parsed as UTC midnight and formatted in UTC: letting the browser
 * shift it into the viewer's zone is how a Monday assessment renders as
 * Sunday for anyone west of the facility.
 */
function formatAssessmentDay(date: string): string {
  const parsed = new Date(`${date}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** Per-record form state the reviewer edits. */
type RecordDraft = {
  dateGiven: string;
  expiresAt: string;
  /** #1104 — the picked term, any vaccine. '' = not picked. */
  term: VaxIntakeTerm | '';
};

type Draft = {
  dogId: string;
  records: Record<string, RecordDraft>;
  reviewNotes: string;
};

function initialDraft(sub: VaxIntakeSubmission): Draft {
  const records: Record<string, RecordDraft> = {};
  for (const r of sub.records) {
    records[r.id] = {
      dateGiven: r.dateGiven ?? '',
      expiresAt: r.proposedExpiresAt ?? '',
      // An unstated term stays EMPTY on purpose. Pre-selecting either
      // option would turn "we don't know" into an answer the reviewer
      // never gave — the exact failure this feature prevents. (`term` is
      // #1104's field; `rabiesTerm` is what an older server sends.)
      term: r.term ?? r.rabiesTerm ?? '',
    };
  }
  return { dogId: sub.matchedDogId ?? '', records, reviewNotes: '' };
}

export function VaxIntake({ config }: VaxIntakeProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  // The facility's REAL inbound address, from the same server config that
  // decides whether this page renders at all. The empty state names it
  // rather than saying "the facility's vaccination address" — a
  // receptionist can only act on an address she can read, and the generic
  // phrasing was pointing at a mailbox that did not exist (P5).
  const { vaxInboundAddress, loading: featuresLoading } = useFeatureAvailability();
  const [filter, setFilter] = useState<VaxIntakeStatus>('pending');
  const [data, setData] = useState<VaxIntakeQueueResponse | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const load = useCallback(
    async (current: VaxIntakeStatus) => {
      const { token } = readSession();
      if (!token) {
        setError('Your session has ended. Sign in again to review records.');
        setIsFetching(false);
        return;
      }
      setIsFetching(true);
      try {
        const payload = await fetchVaxIntakeQueue(apiUrl, token, { status: current });
        setData(payload);
        setDrafts((prev) => {
          const next = { ...prev };
          for (const row of payload.rows) {
            if (!next[row.id]) next[row.id] = initialDraft(row);
          }
          return next;
        });
        setError(null);
      } catch {
        // Copy names the situation and the recovery, not the technology.
        setError("We couldn't load the vaccination inbox. Try again in a moment.");
      } finally {
        setIsFetching(false);
      }
    },
    [apiUrl],
  );

  useEffect(() => {
    void load(filter);
  }, [filter, load]);

  const rows = data?.rows ?? [];

  // #752 — `?focus=<submissionId>` from the dog profile's "Review & enter
  // dates". Scroll to that card once it is on screen; it also gets a ring so
  // the reviewer knows which one they came for.
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');
  useEffect(() => {
    if (!focusId || !rows.some((r) => r.id === focusId)) return;
    globalThis.document
      ?.getElementById(`vax-submission-${focusId}`)
      ?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  }, [focusId, rows]);

  /**
   * #720 — the cards with a clock on them. Derived from the SAME rows the
   * list renders, so the count and the cards can never disagree (P1: one
   * glance, one truth). Only on the pending filter: an approved record is
   * not standing between a dog and the door.
   */
  const assessmentBlocked = useMemo(
    () =>
      filter === 'pending'
        ? rows.filter((r) => r.upcomingAssessment !== null)
        : [],
    [rows, filter],
  );

  return (
    <div className="ferro-admin-page ferro-vax-intake">
      <header className="ferro-detail__header">
        <h1 className="ferro-section__title">Vaccination inbox</h1>
        <p className="ferro-detail__meta">
          Records owners and vets send in — emailed or uploaded in the app —
          read automatically and waiting for you to check them. Nothing reaches
          a dog&rsquo;s record until you approve it.
        </p>
      </header>

      {/* Ted 10-09 — the address, always on the page (not only when the
          queue is empty), with one-tap copy. Or, honestly, that forwarding
          is not on yet. */}
      <VaxForwardingAddress address={vaxInboundAddress} loading={featuresLoading === true} />

      {/* The only thing on this page with a deadline. Rendered only when it
          is true; a permanent "0 assessments" strip is a fabricated zero and
          trains people to stop reading (P5). */}
      {assessmentBlocked.length > 0 ? (
        <p className="ferro-vax-intake__deadline-banner" role="status">
          <strong>
            {assessmentBlocked.length === 1
              ? '1 record still to check'
              : `${assessmentBlocked.length} records still to check`}
          </strong>{' '}
          before an assessment
          {assessmentBlocked.length === 1 ? '' : 's'} booked
          {assessmentBlocked.length === 1 &&
          assessmentBlocked[0]?.upcomingAssessment
            ? ` ${formatAssessmentDay(assessmentBlocked[0].upcomingAssessment.date)}`
            : ' soon'}
          .
        </p>
      ) : null}

      <div className="ferro-compliance__filters" role="tablist" aria-label="Filter records">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            role="tab"
            aria-selected={filter === f.value}
            className={`ferro-compliance__filter${
              filter === f.value ? ' ferro-compliance__filter--active' : ''
            }`}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
            <span className="ferro-compliance__filter-count">
              {data ? data.counts[f.countKey] : '—'}
            </span>
          </button>
        ))}
      </div>

      {error ? (
        <div className="ferro-vax-intake__notice ferro-vax-intake__notice--error" role="alert">
          <p>{error}</p>
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={() => void load(filter)}>
            Try again
          </button>
        </div>
      ) : null}

      {/* Loading: skeletons, never a blank screen or a spinner wall (P4). */}
      {isFetching && !data ? (
        <div className="ferro-vax-intake__list" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="ferro-vax-intake__card ferro-vax-intake__card--skeleton">
              <div className="ferro-skeleton ferro-skeleton--title" />
              <div className="ferro-skeleton ferro-skeleton--line" />
              <div className="ferro-skeleton ferro-skeleton--line" />
            </div>
          ))}
        </div>
      ) : null}

      {/* Empty: teaching copy + what to do first (P2). */}
      {!isFetching && !error && rows.length === 0 ? (
        <div className="ferro-vax-intake__empty">
          <h2 className="ferro-vax-intake__empty-title">
            {filter === 'pending' ? 'Nothing waiting' : 'Nothing here yet'}
          </h2>
          <p>
            {filter === 'pending'
              ? 'Vaccination records owners upload in the app, and records emailed to the facility, show up here — already read — for you to check and approve.'
              : 'Records you approve or reject move here so you can look back at what was decided.'}
          </p>
          {/* Only teach the workflow when we can name the address that
              makes it work. FeatureRoute already keeps this page behind the
              provisioning gate, so this is the belt to that braces — if the
              address is ever unknown we say nothing rather than send a vet
              to a mailbox that doesn't exist (P5). */}
          {filter === 'pending' && vaxInboundAddress ? (
            <p className="ferro-vax-intake__empty-hint">
              Ask a vet&rsquo;s office to email records straight to the
              forwarding address above, instead of faxing or mailing them.
            </p>
          ) : null}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <div className="ferro-vax-intake__list">
          {rows.map((sub) => (
            <SubmissionCard
              key={sub.id}
              apiUrl={apiUrl}
              focused={sub.id === focusId}
              submission={sub}
              draft={drafts[sub.id] ?? initialDraft(sub)}
              busy={busyId === sub.id}
              error={rowError[sub.id] ?? null}
              onDraftChange={(next) =>
                setDrafts((prev) => ({ ...prev, [sub.id]: next }))
              }
              onApprove={async (draft) => {
                const { token } = readSession();
                if (!token) return;
                setBusyId(sub.id);
                setRowError((p) => ({ ...p, [sub.id]: '' }));
                try {
                  await approveVaxIntake(apiUrl, token, sub.id, {
                    dogId: draft.dogId,
                    records: sub.records.map((r) => {
                      const term = effectiveTerm(r, draft.records[r.id]?.term ?? '');
                      return {
                        vaccineName: r.vaccineName,
                        dateGiven: draft.records[r.id]?.dateGiven ?? '',
                        expiresAt: draft.records[r.id]?.expiresAt ?? '',
                        term,
                        // A pre-#1104 server reads only this, and only on
                        // rabies — keep sending it there.
                        rabiesTerm: r.vaccineName === 'rabies' ? term : null,
                      };
                    }),
                    ...(draft.reviewNotes ? { reviewNotes: draft.reviewNotes } : {}),
                  });
                  await load(filter);
                } catch (e) {
                  setRowError((p) => ({
                    ...p,
                    [sub.id]:
                      e instanceof Error && e.message
                        ? e.message
                        : "That didn't save. Check the dates and try again.",
                  }));
                } finally {
                  setBusyId(null);
                }
              }}
              onReject={async (draft) => {
                const { token } = readSession();
                if (!token) return;
                setBusyId(sub.id);
                setRowError((p) => ({ ...p, [sub.id]: '' }));
                try {
                  await rejectVaxIntake(apiUrl, token, sub.id, {
                    ...(draft.reviewNotes ? { reviewNotes: draft.reviewNotes } : {}),
                  });
                  await load(filter);
                } catch {
                  setRowError((p) => ({
                    ...p,
                    [sub.id]: "That didn't save. Try again in a moment.",
                  }));
                } finally {
                  setBusyId(null);
                }
              }}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────

type SubmissionCardProps = {
  apiUrl: string;
  /** #752 — the card the dog profile linked to. */
  focused: boolean;
  submission: VaxIntakeSubmission;
  draft: Draft;
  busy: boolean;
  error: string | null;
  onDraftChange: (next: Draft) => void;
  onApprove: (draft: Draft) => void | Promise<void>;
  onReject: (draft: Draft) => void | Promise<void>;
};

function SubmissionCard({
  apiUrl,
  focused,
  submission: sub,
  draft,
  busy,
  error,
  onDraftChange,
  onApprove,
  onReject,
}: SubmissionCardProps): JSX.Element {
  const [showSource, setShowSource] = useState(false);
  // #752 — the original record, opened full screen next to the fields.
  const [openDoc, setOpenDoc] = useState<VaxDocumentMeta | null>(null);
  const documents = sub.documents ?? [];
  const reviewed = sub.status !== 'pending';

  const chosenDog = useMemo(
    () => sub.candidates.find((c) => c.dogId === draft.dogId) ?? null,
    [sub.candidates, draft.dogId],
  );

  /**
   * Everything blocking approval, in the reviewer's words. Stating these
   * next to a disabled button is what keeps the card from being a dead end
   * (P2) — the reviewer always knows what to do to unblock it.
   */
  const blockers = useMemo(() => {
    const out: string[] = [];
    if (!draft.dogId) out.push('Choose which dog this record belongs to.');
    if (sub.records.length === 0)
      out.push('No vaccination dates were found in this email. Reject it, or add the dates by hand on the dog’s record.');
    for (const r of sub.records) {
      const d = draft.records[r.id];
      const label = VACCINE_LABELS[r.vaccineName];
      if (!d?.dateGiven) out.push(`Enter the date the ${label} was given.`);
      if (!d?.expiresAt) out.push(`Enter when the ${label} expires.`);
      if (termOptionsFor(r).length > 1 && !d?.term)
        out.push(
          `Choose whether the ${VACCINE_IN_SENTENCE[r.vaccineName]} shot is 1-year or 3-year.`,
        );
    }
    return out;
  }, [draft, sub.records]);

  const canApprove = !reviewed && blockers.length === 0 && !busy;

  return (
    <article
      id={`vax-submission-${sub.id}`}
      className={`ferro-vax-intake__card${focused ? ' ferro-vax-intake__card--focus' : ''}`}
    >
      <header className="ferro-vax-intake__card-head">
        <div>
          {/* The dog is the hero (P1, P3): bigger than our labels. */}
          <h2 className="ferro-vax-intake__dog">
            {chosenDog ? chosenDog.dogName : 'Not matched to a dog yet'}
          </h2>
          {/* Null when the server narrowed customer PII out of the payload
              (#537) — render nothing rather than an empty line or the word
              "null". */}
          {chosenDog?.customerName ? (
            <p className="ferro-vax-intake__owner">{chosenDog.customerName}</p>
          ) : null}
        </div>
        <MatchBadge status={sub.matchStatus} />
      </header>

      {/* #720 — the clock on this card, stated before the paperwork. A dog
          with an assessment booked is the case Kylie was hand-typing dates
          for the morning of; the deadline is the reason the card matters,
          so it leads (P2). Absent when there is no assessment — no badge,
          no "none" row, nothing to read past. */}
      {sub.upcomingAssessment && !reviewed ? (
        <p className="ferro-vax-intake__deadline" role="status">
          <strong>
            Assessment {formatAssessmentDay(sub.upcomingAssessment.date)}
            {sub.upcomingAssessment.startTime
              ? ` at ${sub.upcomingAssessment.startTime}`
              : ''}
          </strong>{' '}
          — check this before {chosenDog ? chosenDog.dogName : 'the dog'} arrives.
        </p>
      ) : null}

      <dl className="ferro-vax-intake__meta">
        {/* #720 — how it arrived. An in-app upload and an emailed record
            are checked the same way but chased differently: you can reply
            to an email. */}
        <div>
          <dt>How it arrived</dt>
          <dd>{SOURCE_LABELS[sub.source] ?? SOURCE_LABELS.email}</dd>
        </div>
        {/* Sender identity is nulled for callers the server narrows
            (#537). Say "hidden" rather than rendering a blank row — an
            empty value next to a label reads as missing data (P5). */}
        <div>
          <dt>From</dt>
          <dd>{formatSender(sub.source, sub.fromName, sub.fromEmail)}</dd>
        </div>
        <div>
          <dt>Received</dt>
          <dd>{formatDateTime(sub.receivedAt)}</dd>
        </div>
        {sub.subject && sub.source === 'email' ? (
          <div>
            <dt>Subject</dt>
            <dd>{sub.subject}</dd>
          </div>
        ) : null}
      </dl>

      {sub.matchReason ? (
        <p className="ferro-vax-intake__match-reason">{sub.matchReason}</p>
      ) : null}

      {/* #752 — read the dates off the RECORD, not off our parse of it.
          The button sits above the fields it is checked against. */}
      {documents.length > 0 ? (
        <div className="ferro-vax-intake__documents">
          {documents.map((d, i) =>
            d.deleted ? (
              // #1182 — rejected ⇒ deleted. A button here would open a 410.
              <p
                key={d.id}
                className="ferro-vax-intake__doc-deleted ferro-muted"
                data-testid="vax-intake-document-deleted"
              >
                {documents.length > 1
                  ? `Page ${i + 1} was deleted when this record was rejected.`
                  : 'The original record was deleted when this was rejected.'}
              </p>
            ) : (
              <button
                key={d.id}
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-vax-intake__view-doc"
                onClick={() => setOpenDoc(d)}
                data-testid="vax-intake-view-document"
              >
                {documents.length > 1 ? `View original, page ${i + 1}` : 'View original record'}
              </button>
            ),
          )}
        </div>
      ) : null}
      {openDoc ? (
        <VaxDocumentViewer
          apiUrl={apiUrl}
          submissionId={sub.id}
          document={openDoc}
          title={`${chosenDog ? chosenDog.dogName : 'Unmatched'} — vaccination record`}
          onClose={() => setOpenDoc(null)}
        />
      ) : null}

      {/* Parse honesty (P5): "no attachment" and "couldn't read it" are
          different situations and must not look the same. */}
      {sub.parseStatus !== 'ok' ? (
        <p className="ferro-vax-intake__notice ferro-vax-intake__notice--stale">
          {sub.parseStatus === 'no_document'
            ? 'This email had no attachment to read a record from. Check the message, then reject it or add the dates by hand.'
            : "We couldn't read a vaccination record from this email. Check the attachment yourself before approving anything."}
        </p>
      ) : null}

      {!reviewed ? (
        <label className="ferro-vax-intake__field">
          <span className="ferro-vax-intake__label">Dog</span>
          <select
            className="ferro-vax-intake__select"
            value={draft.dogId}
            onChange={(e) => onDraftChange({ ...draft, dogId: e.target.value })}
          >
            <option value="">Choose a dog…</option>
            {sub.candidates.map((c) => (
              <option key={c.dogId} value={c.dogId}>
                {c.customerName ? `${c.dogName} — ${c.customerName}` : c.dogName}
              </option>
            ))}
          </select>
          {sub.candidates.length === 0 ? (
            <span className="ferro-vax-intake__hint">
              We don&rsquo;t recognise this sender, so we can&rsquo;t suggest a
              dog. Open the dog&rsquo;s profile, choose Add a vaccination under
              Health &amp; compliance and enter these dates there, then reject
              this so it leaves the queue.
            </span>
          ) : null}
        </label>
      ) : null}

      {sub.records.map((r) => (
        <RecordRow
          key={r.id}
          record={r}
          draft={draft.records[r.id] ?? { dateGiven: '', expiresAt: '', term: '' }}
          disabled={reviewed || busy}
          onChange={(next) =>
            onDraftChange({
              ...draft,
              records: { ...draft.records, [r.id]: next },
            })
          }
        />
      ))}

      {sub.parseRawText ? (
        <div className="ferro-vax-intake__source">
          <button
            type="button"
            className="ferro-vax-intake__disclosure"
            aria-expanded={showSource}
            onClick={() => setShowSource((v) => !v)}
          >
            {showSource ? 'Hide' : 'Show'} what the record says
          </button>
          {showSource ? (
            <blockquote className="ferro-vax-intake__quote">
              {/* Plain text only — this is untrusted document content. */}
              {sub.parseRawText}
              {sub.parseNotes ? (
                <footer className="ferro-vax-intake__quote-note">{sub.parseNotes}</footer>
              ) : null}
            </blockquote>
          ) : null}
        </div>
      ) : null}

      {reviewed ? (
        <p className="ferro-vax-intake__reviewed">
          {sub.status === 'approved' ? 'Approved' : 'Rejected'}
          {sub.reviewedAt ? ` on ${formatDateTime(sub.reviewedAt)}` : ''}
          {sub.reviewNotes ? ` — ${sub.reviewNotes}` : ''}
        </p>
      ) : (
        <>
          <label className="ferro-vax-intake__field">
            <span className="ferro-vax-intake__label">Note (optional)</span>
            <input
              type="text"
              className="ferro-vax-intake__input"
              value={draft.reviewNotes}
              disabled={busy}
              placeholder="Anything the next person should know"
              onChange={(e) => onDraftChange({ ...draft, reviewNotes: e.target.value })}
            />
          </label>

          {blockers.length > 0 ? (
            <ul className="ferro-vax-intake__blockers">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          ) : null}

          {error ? (
            <p className="ferro-vax-intake__notice ferro-vax-intake__notice--error" role="alert">
              {error}
            </p>
          ) : null}

          <div className="ferro-vax-intake__actions">
            {/* Exactly one filled-primary per card (P2). */}
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              disabled={!canApprove}
              onClick={() => void onApprove(draft)}
            >
              {busy ? 'Saving…' : 'Approve & add to record'}
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              disabled={busy}
              onClick={() => void onReject(draft)}
            >
              Reject
            </button>
          </div>
        </>
      )}
    </article>
  );
}

function MatchBadge({ status }: { status: VaxIntakeSubmission['matchStatus'] }): JSX.Element {
  const label =
    status === 'matched'
      ? 'Matched'
      : status === 'ambiguous'
        ? 'Needs a dog picked'
        : 'No dog found';
  return (
    <span className={`ferro-compliance__badge ferro-vax-intake__badge--${status}`}>
      {label}
    </span>
  );
}

function RecordRow({
  record,
  draft,
  disabled,
  onChange,
}: {
  record: VaxIntakeRecord;
  draft: RecordDraft;
  disabled: boolean;
  onChange: (next: RecordDraft) => void;
}): JSX.Element {
  const options = termOptionsFor(record);
  const asksTerm = options.length > 1;
  const term = effectiveTerm(record, draft.term);
  const stagedTerm = record.term ?? record.rabiesTerm;
  const prompts = record.flags.map((f) => FLAG_PROMPTS[f]).filter(Boolean) as string[];

  /**
   * Live disagreement between the term the reviewer picked and the
   * expiry currently in the box. The staged `document_derived_mismatch`
   * flag only covers what ARRIVED; this covers what the reviewer is
   * about to submit — including the case the flag can't see, where the
   * document stated no term and the reviewer supplies one against a
   * printed expiry. Warn, never overwrite: the vet's printed date beats
   * our arithmetic, and silently clobbering it would be the same bug
   * pointing the other way.
   */
  const termExpiryConflict =
    term && draft.dateGiven && draft.expiresAt
      ? (() => {
          const fromTerm = addYears(draft.dateGiven, termYears(term));
          return fromTerm && fromTerm !== draft.expiresAt ? fromTerm : null;
        })()
      : null;

  return (
    <section className="ferro-vax-intake__record">
      <h3 className="ferro-vax-intake__vaccine">{VACCINE_LABELS[record.vaccineName]}</h3>

      <div className="ferro-vax-intake__record-grid">
        <label className="ferro-vax-intake__field">
          <span className="ferro-vax-intake__label">Date given</span>
          <input
            type="date"
            className="ferro-vax-intake__input"
            value={draft.dateGiven}
            disabled={disabled}
            onChange={(e) => onChange({ ...draft, dateGiven: e.target.value })}
          />
        </label>

        <label className="ferro-vax-intake__field">
          <span className="ferro-vax-intake__label">Expires</span>
          <input
            type="date"
            className="ferro-vax-intake__input"
            value={draft.expiresAt}
            disabled={disabled}
            onChange={(e) => onChange({ ...draft, expiresAt: e.target.value })}
          />
          {/* P5: say where the number came from. A calculated date and a
              date printed by the vet are not the same kind of fact. */}
          <span className="ferro-vax-intake__hint">
            {record.expirySource === 'document'
              ? 'From the record'
              : record.expirySource === 'derived'
                ? `Worked out from the ${termLabel(stagedTerm ?? 'one_year')} term${
                    options.length === 1 ? ' your facility uses' : ''
                  } — check it`
                : 'Not on the record — enter it'}
          </span>
        </label>
      </div>

      {asksTerm ? (
        <fieldset className="ferro-vax-intake__terms">
          <legend className="ferro-vax-intake__label">
            {record.vaccineName === 'rabies'
              ? 'Rabies type'
              : `${VACCINE_LABELS[record.vaccineName]} term`}{' '}
            <span className="ferro-vax-intake__required">required</span>
          </legend>
          <div className="ferro-vax-intake__term-options">
            {options.map((years) => {
              const value = yearsToTerm(years);
              return (
                <label key={value} className="ferro-vax-intake__term">
                  <input
                    type="radio"
                    name={`${record.vaccineName}-term-${record.id}`}
                    value={value}
                    checked={draft.term === value}
                    disabled={disabled}
                    onChange={() => {
                      // Changing the term invalidates a previously derived
                      // expiry — recompute it so the two can never disagree
                      // on screen (P1: two numbers that could disagree must
                      // come from the same source). A printed expiry is
                      // never overwritten.
                      const next: RecordDraft = { ...draft, term: value };
                      if (record.expirySource !== 'document' && draft.dateGiven) {
                        next.expiresAt = addYears(draft.dateGiven, years);
                      }
                      onChange(next);
                    }}
                  />
                  <span>{termLabel(value)}</span>
                </label>
              );
            })}
          </div>
          {stagedTerm == null ? (
            <p className="ferro-vax-intake__hint">
              The record doesn&rsquo;t say which. If you&rsquo;re not sure, check
              with the vet before approving — a 1-year and a 3-year shot expire
              two years apart.
            </p>
          ) : null}
        </fieldset>
      ) : null}

      {/* Warned for ANY term the record will be approved under — picked on
          a choice, or implied by a fixed policy — so a printed 6-month
          Bordetella under a "1 year" rule is visible before Approve. */}
      {termExpiryConflict && term ? (
        <p className="ferro-vax-intake__hint" role="status">
          A {termLabel(term)} shot given {draft.dateGiven} would expire{' '}
          {termExpiryConflict}, not {draft.expiresAt}. Keep the date printed on
          the record if that&rsquo;s what it says — otherwise fix it.
        </p>
      ) : null}

      {prompts.length > 0 ? (
        <ul className="ferro-vax-intake__prompts">
          {prompts.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

/**
 * Client-side convenience mirror of the server's expiry arithmetic, used
 * only to re-fill the form when the reviewer picks a term. The SERVER is
 * authoritative: it validates whatever is submitted and rejects a record
 * the facility asks a term for when none is given. Feb 29 clamps to Feb 28, matching
 * `addYearsUtc` in services/vax-expiry.ts.
 */
export function addYears(isoDate: string, years: number): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!m) return '';
  const y = Number(m[1]) + years;
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const candidate = new Date(Date.UTC(y, mo - 1, d));
  const safe =
    candidate.getUTCMonth() === mo - 1 ? candidate : new Date(Date.UTC(y, mo, 0));
  return safe.toISOString().slice(0, 10);
}
